import * as React from 'react'
import { cn } from '@/lib/utils'
import { Input, type InputProps } from '@/design-system/atoms/Input'
import { Icon } from '@/design-system/atoms/Icon'
import { IconButton } from '@/design-system/atoms/IconButton'

export interface SearchBarProps extends Omit<
  InputProps,
  'type' | 'leftIcon' | 'rightIcon'
> {
  onClear?: () => void
  value?: string
  showClear?: boolean
  debounceMs?: number
  onDebouncedChange?: (value: string) => void
}

export const SearchBar = React.forwardRef<HTMLInputElement, SearchBarProps>(
  (
    {
      className,
      value,
      defaultValue = '',
      onChange,
      onClear,
      showClear = true,
      debounceMs,
      onDebouncedChange,
      placeholder = 'Buscar...',
      ...rest
    },
    ref
  ) => {
    const [internal, setInternal] = React.useState<string>(String(defaultValue))
    const v = value ?? internal
    const inputRef = React.useRef<HTMLInputElement | null>(null)

    React.useImperativeHandle(ref, () => inputRef.current as HTMLInputElement)

    React.useEffect(() => {
      if (!debounceMs || !onDebouncedChange) return
      const id = setTimeout(() => onDebouncedChange(v), debounceMs)
      return () => clearTimeout(id)
    }, [v, debounceMs, onDebouncedChange])

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (value === undefined) setInternal(e.target.value)
      onChange?.(e)
    }

    const handleClear = () => {
      if (value === undefined) setInternal('')
      onClear?.()
      inputRef.current?.focus()
    }

    const hasValue = v.length > 0

    return (
      <Input
        ref={inputRef}
        type="search"
        role="searchbox"
        placeholder={placeholder}
        value={v}
        onChange={handleChange}
        leftIcon={<Icon name="search" size={16} />}
        rightIcon={
          showClear && hasValue ? (
            <IconButton
              variant="ghost"
              size="xs"
              icon={<Icon name="x" size={14} />}
              aria-label="Limpar busca"
              onClick={handleClear}
            />
          ) : undefined
        }
        // O Chrome desenha `::-webkit-search-cancel-button` em `type="search"`,
        // e o DS já injeta o próprio botão de limpar: apareciam DOIS ✕, com
        // comportamentos diferentes (o nativo não dispara `onClear`).
        className={cn(
          '[&::-webkit-search-cancel-button]:appearance-none',
          className
        )}
        {...rest}
      />
    )
  }
)

SearchBar.displayName = 'SearchBar'
